import React, { useContext, useEffect, useState } from 'react';
import './DocumentsTable.scss';
import globalConstants from 'src/constants/globalConstants';
import { Table, TableButton } from '@vwfs-bronson/bronson-react';
import { ILoanAgreements, IStatements } from '../../../context/DocCenterContext.interface';
import { INotificationContext } from '../../../context/NotificationContext.interface';
import { NotificationContext } from '../../../context/NotificationContext';
import { getStatementsService, getLoanAgreementService } from '../../../services/message-center/get-documents';
import { useAuthContext } from '../../../context/AuthContext';
import i18n from '../../../localization/i18n';
import * as rk from '../../../localization/resourceKeys';
import { uuidv4, b64toBlob, logDownloadedDocumentSize } from '../../../utility/Utilities';

type Props = {
  file: IStatements | ILoanAgreements;
};

const TableRow = (props: Props) => {
  const { file } = props;
  const { addNotification, notifications, removeNotification } = useContext(
    NotificationContext
  ) as INotificationContext;
  const [id] = useState<string>(uuidv4());
  const [status, setStatus] = useState<string>('notDownloading');
  const { state: authState } = useAuthContext();
  let docDate;
  if (file.InvoiceDate) {
    docDate = new Date(file.InvoiceDate).toLocaleDateString('en-us', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } else if (file.CreateDate) {
    docDate = new Date(file.CreateDate).toLocaleDateString('en-us', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }

  useEffect(() => {
    const find = notifications.find((c) => c.id === id);
    let timer: ReturnType<typeof setTimeout>;
    if (!find) {
      if (status === 'error' || status === 'success') {
        setStatus('notDownloading');
      }
    } else {
      timer = setTimeout(() => {
        removeNotification(id);
      }, 5500);
    }
    return () => clearTimeout(timer);
  }, [notifications]);

  useEffect(() => {
    // FILTERING RESETS DOWNLOAD STATE
    setStatus('notDownloading');
  }, [file]);

  const handleClick = async (clickedDocument: IStatements) => {
    try {
      setStatus('downloading');
      let response;

      if (clickedDocument?.InvoiceDate) {
        response = await getStatementsService(authState, clickedDocument);
      } else if (clickedDocument?.DocumentFileTypeName) {
        const documentType = clickedDocument?.DocumentName?.toLowerCase().includes('autopay') ? 'Autopay' : 'Statement';
        response = await getLoanAgreementService(authState, clickedDocument, documentType);
      }

      if (response.IsSuccess) {
        // DOWNLOAD THE FILE
        // TODO - File name should be changed
        const fileName = 'Document.pdf';

        // Create the anchor element to trigger the download.
        const downloadLink = document.createElement('a');
        downloadLink.style.display = 'none';
        document.body.appendChild(downloadLink);

        // Generate BLOB.
        const blobData = b64toBlob(
          clickedDocument?.InvoiceDate ? response.Invoice : response.Document,
          'application/pdf'
        );
        logDownloadedDocumentSize(blobData);

        // Add the href attribute with the blob data and click it.
        downloadLink.href = window.URL.createObjectURL(blobData);
        downloadLink.setAttribute('download', fileName);
        downloadLink.click();
        setStatus('success');

        // Clean up the element we created for the download.
        window.URL.revokeObjectURL(downloadLink.href);
        document.body.removeChild(downloadLink);

        addNotification({
          message: i18n.t(rk.DOCUMENT_CENTER_NOTIFICATION_SUCCESS),
          style: 'success',
          action: () => true,
          id,
        });
      } else {
        addNotification({
          message: i18n.t(rk.DOCUMENT_CENTER_NOTIFICATION_ERROR),
          style: 'error',
          action: () => {
            handleClick(clickedDocument);
          },
          id,
        });
        setStatus('error');
      }
    } catch (error) {
      addNotification({
        message: i18n.t(rk.DOCUMENT_CENTER_NOTIFICATION_ERROR),
        style: 'error',
        action: () => {
          handleClick(clickedDocument);
        },
        id,
      });
      setStatus('error');
    }
  };

  const getFileName = () => {
    let fileName;
    if (file?.DocumentName?.toLowerCase().includes('autopay')) {
      fileName = globalConstants.DOCUMENT_MAPPING.AutopayLetter.value;
    } else if (file?.InvoiceDate) {
      fileName = globalConstants.DOCUMENT_MAPPING.Statements.value;
    } else if (file?.DocumentFileTypeName) {
      fileName = globalConstants.DOCUMENT_MAPPING.Loan.value;
    }
    return fileName;
  };

  const iconName = (() => {
    if (status === 'notDownloading') return 'semantic-download';
    if (status === 'downloading') return 'semantic-reload';
    if (status === 'success') return 'semantic-checkmark';

    return 'semantic-crossed-out';
  })();

  return (
    <>
      <Table.Tr>
        <Table.Td>{docDate}</Table.Td>
        <Table.Td data-columnheader='Download'>
          {status === 'downloading' ? (
            <div className='c-spinner  c-spinner--small c-spinner-table' aria-live='polite' aria-busy='true'>
              <div className='c-spinner__loader'>
                <span className='c-spinner__element' />
                <span className='c-spinner__element' />
                <span className='c-spinner__element' />
                <span className='c-spinner__element' />
                <span className='c-spinner__element' />
                <span className='c-spinner__element' />
                <span className='c-spinner__element' />
                <span className='c-spinner__element' />
              </div>
            </div>
          ) : (
            <TableButton
              className='doc-table-col__download'
              icon={iconName}
              onClick={() => {
                if (status === 'notDownloading') handleClick(file);
              }}
            />
          )}
        </Table.Td>
        <Table.Td>{getFileName()}</Table.Td>
      </Table.Tr>
    </>
  );
};

export default TableRow;
